import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ContentfulService } from '../contentful/contentful.service';
import {
  ProductFields,
  formatWeightLabel,
} from '../contentful/product.model';
import { CartStore } from '../cart/cart.store';
import { SeoService } from '../seo/seo.service';
import { CategoryNavComponent } from '../categories/category-nav.component';

interface ProductDetail {
  id: string;
  name: string;
  sku: string;
  imageUrls: string[];
  weightLabel: string | null;
  price: number | null;
  wholesalePrice: number | null;
  package: string | null;
  units: string | null;
  descriptionHtml: string;
  characteristicsHtml: string;
  categoryName: string;
  categorySlug: string;
}

type RichTextNode = {
  nodeType: string;
  value?: string;
  content?: RichTextNode[];
  marks?: Array<{ type: string }>;
};

function richTextToHtml(doc: RichTextNode | null | undefined): string {
  if (!doc?.content) return '';
  return doc.content.map(n => nodeToHtml(n)).join('');
}

function nodeToHtml(node: RichTextNode): string {
  if (node.nodeType === 'text') {
    let text = node.value ?? '';
    if (node.marks?.some(m => m.type === 'bold'))   text = `<strong>${text}</strong>`;
    if (node.marks?.some(m => m.type === 'italic')) text = `<em>${text}</em>`;
    return text;
  }
  const inner = (node.content ?? []).map(n => nodeToHtml(n)).join('');
  switch (node.nodeType) {
    case 'paragraph':       return inner.trim() ? `<p>${inner}</p>` : '';
    case 'heading-1':       return `<h1>${inner}</h1>`;
    case 'heading-2':       return `<h2>${inner}</h2>`;
    case 'heading-3':       return `<h3>${inner}</h3>`;
    case 'unordered-list':  return `<ul>${inner}</ul>`;
    case 'ordered-list':    return `<ol>${inner}</ol>`;
    case 'list-item':       return `<li>${inner}</li>`;
    case 'hr':              return '<hr/>';
    default:                return inner;
  }
}

@Component({
  selector: 'app-product',
  imports: [DecimalPipe, RouterModule, MatSnackBarModule, CategoryNavComponent],
  templateUrl: './product.component.html',
  styleUrl: './product.component.scss',
})
export class ProductComponent implements OnInit {
  product        = signal<ProductDetail | null>(null);
  loading        = signal(true);
  error          = signal(false);
  activeImage    = signal(0);
  /** Draft quantity used only before the product is in the cart. */
  quantity       = signal(1);
  addedToCart    = signal(false);

  readonly cartQty = computed(() => {
    const p = this.product();
    if (!p) return 0;
    return this.cartStore.quantityOf(p.id, p.weightLabel);
  });

  readonly displayQty = computed(() => this.cartQty() || this.quantity());

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly contentfulService: ContentfulService,
    private readonly cartStore: CartStore,
  ) {}

  private readonly snackBar = inject(MatSnackBar);
  private readonly seo = inject(SeoService);

  ngOnInit(): void {
    const sku = this.route.snapshot.paramMap.get('sku') ?? '';
    if (!sku) { this.router.navigate(['/']); return; }

    this.contentfulService
      .getEntries<ProductFields>({
        contentType: 'product',
        query: { 'fields.sku': sku, include: 2 },
        limit: 1,
      })
      .subscribe({
        next: (res) => {
          const item = res.items[0];
          if (!item) {
            this.error.set(true);
            this.loading.set(false);
            this.seo.setNotFound();
            return;
          }

          // Build asset map
          type IncludesShape = { includes?: { Asset?: Array<{ sys: { id: string }; fields: { file?: { url?: string } } }>; Entry?: Array<{ sys: { id: string }; fields: Record<string, unknown> }> } };
          const inc = (res as unknown as IncludesShape).includes;
          const assetMap = new Map<string, string>();
          for (const a of inc?.Asset ?? []) {
            const url = a.fields?.file?.url;
            if (url) assetMap.set(a.sys.id, url.startsWith('//') ? `https:${url}` : url);
          }
          const entryMap = new Map<string, Record<string, unknown>>();
          for (const e of inc?.Entry ?? []) {
            entryMap.set(e.sys.id, e.fields);
          }

          const f = item.fields;

          // Images
          const imgLinks = f['images'] as Array<{ sys: { id: string } }> | undefined;
          const imageUrls = (imgLinks ?? []).map(l => assetMap.get(l.sys.id) ?? '').filter(Boolean);

          const units = (f['units'] as string | undefined) ?? null;
          const weightLabel = formatWeightLabel(
            f['weight'] as string | undefined,
            units ?? undefined,
          );

          // Category
          const catLink = f['category'] as { sys: { id: string } } | undefined;
          const catFields = catLink ? entryMap.get(catLink.sys.id) : undefined;
          const categoryName = catFields?.['name'] as string ?? '';
          const categorySlug = catFields?.['slug'] as string ?? '';

          const product = {
            id: item.sys.id,
            name:                f['name'] as string,
            sku:                 (f['sku'] as string | undefined) ?? '',
            imageUrls,
            weightLabel,
            price:               (f['price'] as number | undefined) ?? null,
            wholesalePrice:      (f['wholesalePrice'] as number | undefined) ?? null,
            package:             (f['package'] as string | undefined) ?? null,
            units,
            descriptionHtml:     richTextToHtml(f['description'] as RichTextNode),
            characteristicsHtml: richTextToHtml(f['characteristics'] as RichTextNode),
            categoryName,
            categorySlug,
          };
          this.product.set(product);
          this.seo.setProduct({
            name: product.name,
            sku: product.sku,
            categoryName,
            weightLabel,
            units: product.units,
            package: product.package,
            price: product.price,
            wholesalePrice: product.wholesalePrice,
            imageUrl: imageUrls[0] ?? null,
          });
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
          this.seo.setNotFound();
        },
      });
  }

  get activePrice(): number | null {
    return this.product()?.price ?? null;
  }

  changeQty(delta: number): void {
    const p = this.product();
    if (!p) return;

    const inCart = this.cartQty();
    if (inCart > 0) {
      this.cartStore.updateQty(p.id, p.weightLabel, inCart + delta);
      if (inCart + delta <= 0) this.quantity.set(1);
      return;
    }

    const next = this.quantity() + delta;
    if (next >= 1) this.quantity.set(next);
  }

  addToCart(): void {
    const p = this.product();
    if (!p || this.cartQty() > 0) return;

    const qty = this.quantity();
    this.cartStore.add({
      productId: p.id,
      sku: p.sku,
      name: p.name,
      imageUrl: p.imageUrls[0] ?? null,
      weightLabel: p.weightLabel,
      unit: p.units,
      price: p.price,
    }, qty);

    this.addedToCart.set(false);
    requestAnimationFrame(() => this.addedToCart.set(true));
    setTimeout(() => this.addedToCart.set(false), 1600);

    const message = qty > 1
      ? `${p.name} (${qty} шт.) додано до кошика`
      : `${p.name} додано до кошика`;
    this.snackBar.open(message, 'OK', {
      duration: 2500,
      panelClass: 'snack-success',
      horizontalPosition: 'end',
      verticalPosition: 'bottom',
    });
  }
}
